import React, { useState, useEffect, useContext } from 'react';
import { useSelector } from 'react-redux';
import { Button, Table, TableBody, TableCell, TableHead, TableRow, TextField, Select, MenuItem, Checkbox, FormControlLabel, IconButton, Tooltip } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { GlobalContext } from '../../../App';
import { getFieldOptions } from '../plagins/data/commponentOptions';

function ensureArray(value) {
  // אם כבר מערך, החזר כמו שהוא
  if (Array.isArray(value)) return value;

  // אם מחרוזת, נסה לפענח JSON
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [parsed];
    } catch {
      // אם נכשל הפענוח, החזר מערך ריק
      return [];
    }
  }

  // אם הגיע אובייקט בודד (לא מערך), אפשר לשים אותו במערך [object]
  // או להחזיר מערך ריק, תלוי בהיגיון העסקי
  return [];
}


const PaymentsManagement = ({ onPaymentsChange, defaultPayments = [] }) => {
  const { permission } = useContext(GlobalContext); // קבלת permission מ-GlobalContext
  const lockedPermissions = []; // סט הרשאות עבור רשומות נעולות
  const requiredPermissions = [1]; // סט הרשאות עבור רשומות חובה

  // קבלת ערכים מהסטור
  const optionsFields = useSelector((state) => state.dataOptionsFieldsHe.data);
  const columns = useSelector((state) => state.columnsFormPaymentsManagement.data).filter((column) => column.name !== 'locked' && column.name !== 'required' && column.name !== 'customPaymentType'); // הסתרת השדות נעול, חובה, וסוג תשלום מותאם אישית מהעמודות

  const paymentTypeOptions = getFieldOptions('priceDefinition', optionsFields) || [];

  // פונקציות לבדיקת הרשאות
  const hasLockedPermission = () => permission === 0 || lockedPermissions.includes(permission);
  const hasRequiredPermission = () => permission === 0 || requiredPermissions.includes(permission);

  // התחלת הרשומות עם או בלי ברירת מחדל
  const safePayments = ensureArray(defaultPayments);

  const [paymentRows, setPaymentRows] = useState(
    safePayments.length > 0
      ? safePayments
      : [
          {
            paymentDate: '',
            paymentType: '',
            customPaymentType: '',
            paymentAmount: '',
            receiptDocuments: [],
            isPaymentComplete: false,
            locked: false,
            required: false,
          },
        ]
  );

  const handleInputChange = (index, field, value) => {
    const updatedRows = [...paymentRows];
    updatedRows[index][field] = value;
    console.log(updatedRows);
    
    setPaymentRows(updatedRows);
  };

  const addNewRow = () => {
    setPaymentRows([...paymentRows, { paymentDate: '', paymentType: '', customPaymentType: '', paymentAmount: '', receiptDocuments: [], isPaymentComplete: false, locked: false, required: false }]);
  };

  const handleDeleteRow = (index) => {
    const rowToDelete = paymentRows[index];

    // אם הרשומה נעולה ואין הרשאות מתאימות
    if (rowToDelete.locked && !hasLockedPermission()) {
      alert('לא ניתן למחוק רשומה נעולה.');
      return;
    }

    // אם הרשומה חובה ואין הרשאות מתאימות
    if (rowToDelete.required && !hasRequiredPermission()) {
      alert('לא ניתן למחוק רשומה חובה.');
      return;
    }

    const updatedRows = paymentRows.filter((_, i) => i !== index);
    setPaymentRows(updatedRows);
  };

  // עדכון ה-DIV האב עם הערכים בכל שינוי
  useEffect(() => {
    onPaymentsChange(paymentRows); // שליחת הערכים ל-props
  }, [paymentRows, onPaymentsChange]);

  return (
    <div style={{ width: '100%' }}>
      <Table style={{ width: '100%', tableLayout: 'auto' }}>
        <TableHead>
          <TableRow>
            {columns.map((column) => (
              <TableCell key={column.id} style={{ minWidth: column.width }}>
                {column.label}
              </TableCell>
            ))}
            <TableCell style={{ minWidth: '50px' }}>פעולות</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {paymentRows?.map((row, rowIndex) => (
            <TableRow key={rowIndex}>
              <TableCell>
                <TextField type="date" value={row.paymentDate} onChange={(e) => handleInputChange(rowIndex, 'paymentDate', e.target.value)} fullWidth disabled={row.locked && !hasLockedPermission()} />
              </TableCell>
              <TableCell>
                <Select value={row.paymentType} onChange={(e) => handleInputChange(rowIndex, 'paymentType', e.target.value)} fullWidth disabled={(row.locked && !hasLockedPermission()) || (row.required && !hasRequiredPermission())}>
                  {paymentTypeOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
                {row.paymentType === 'other' && <TextField placeholder="הזן סוג תשלום מותאם" value={row.customPaymentType} onChange={(e) => handleInputChange(rowIndex, 'customPaymentType', e.target.value)} fullWidth style={{ marginTop: '10px' }} disabled={row.locked && !hasLockedPermission()} />}
              </TableCell>
              <TableCell>
                <TextField type="number" value={row.paymentAmount} onChange={(e) => handleInputChange(rowIndex, 'paymentAmount', parseFloat(e.target.value))} fullWidth disabled={(row.locked && !hasLockedPermission()) || (row.required && !hasRequiredPermission())} />
              </TableCell>
              <TableCell>
                <TextField type="text" value={row.receiptDocuments} onChange={(e) => handleInputChange(rowIndex, 'receiptDocuments', e.target.value)} fullWidth disabled={row.locked && !hasLockedPermission()} />
              </TableCell>
              <TableCell>
                <FormControlLabel control={<Checkbox checked={row.isPaymentComplete} onChange={(e) => handleInputChange(rowIndex, 'isPaymentComplete', e.target.checked)} disabled={row.locked && !hasLockedPermission()} />} label="גמר תשלום" />
              </TableCell>
              <TableCell style={{ minWidth: '50px', textAlign: 'center' }}>
                <Tooltip title={row.locked ? (hasLockedPermission() ? 'הסר למרות שנעול' : 'רשומה נעולה, לא ניתן למחוק') : row.required ? (hasRequiredPermission() ? 'הסר למרות שחובה' : 'רשומת חובה, לא ניתן למחוק') : 'מחיקה'}>
                  <span>
                    <IconButton onClick={() => handleDeleteRow(rowIndex)} disabled={row.locked && (!hasLockedPermission() || !hasRequiredPermission())}>
                      <DeleteIcon color={(row.locked && !hasLockedPermission()) || (row.required && !hasRequiredPermission()) ? 'disabled' : 'error'} />
                    </IconButton>
                  </span>
                </Tooltip>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Button onClick={addNewRow} variant="contained" color="primary" style={{ marginTop: '10px' }}>
        הוסף תשלום
      </Button>
    </div>
  );
};

export default PaymentsManagement;
